import React, {useCallback, useState} from 'react';
import dayjs from 'dayjs';
import {
  Avatar,
  Box,
  Button,
  Card,
  CardContent,
  Divider,
  Grid,
  IconButton,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  MenuItem,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import {useQuery, useQueryClient} from '@tanstack/react-query';
import AudioFileIcon from '@mui/icons-material/AudioFile';
import SendIcon from '@mui/icons-material/Send';
import {AxiosResponse} from 'axios';
import DescriptionBox from './DescriptionBox';
import DescriptionIcon from '@mui/icons-material/Description';
import {
  getTaskCountApiPath,
  myTaskList,
} from '../../../../react_query/tasks/ListTasks';
import {apiGet, apiPatch, apiPost} from '../../../../axiosConfig';
import FileUploads from '../../../../components/FileUpload';
import DialogApiRequestStatus from '../../../../components/dialogs/DialogApiRequestStatus';
import {useStoreSelector} from '../../../../store/configstore';
import {userIdSelector} from '../../../../store/Slices/userSlice';
import {ITaskManual} from '../../../../dto/Task.dto';
import {IApiDataResponse} from '../../../../dto/API.dto';
import DialogConfirmSave from '../../../../components/dialogs/DialogConfirmSave';
import IconClose from '../../../../asset/icons/icon_close.svg';
type Props = {
  setOpen: (val: boolean) => void;
  id: number;
};
const ManualTask = ({setOpen, id}: Props) => {
  const queryClient = useQueryClient();
  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
  const [taskDescription, setTaskDescription] = useState<string>();
  const [isClose, setIsClose] = useState(false);

  const [apiState, setApiState] = useState({status: '', error: null});
  const [apiStateCancel, setApiStateCancel] = useState({
    status: '',
    error: null,
  });
  const userId = useStoreSelector(userIdSelector);

  const {data} = useQuery({
    queryFn: () =>
      apiGet(`/v1/task/${id}/manual`).then(
        (res: AxiosResponse<IApiDataResponse<ITaskManual>>) => res.data.data,
      ),
    queryKey: [`/v1/task/${id}/manual`],
  });
  const handleSubmit = () => {
    setApiState({status: 'pending', error: null});
    const fd = new FormData();
    fd.append('task_description', taskDescription ?? '');
    if (selectedFiles.length > 0) {
      selectedFiles.forEach((file, index) => {
        fd.append(`files[${index}]`, file);
      });
    }
    apiPost(`/v1/task/${id}/manual`, fd)
      .then(() => {
        setApiState({status: 'success', error: null});
        queryClient.invalidateQueries({
          queryKey: [myTaskList('')],
          refetchType: 'active',
        });
        queryClient.invalidateQueries({
          queryKey: [getTaskCountApiPath()],
          refetchType: 'active',
        });
      })
      .catch(error => {
        setApiState({status: 'error', error});
      });
  };
  const handleEdit = useCallback(() => {
    setApiState({status: '', error: null});
  }, []);

  const handleClose = () => {
    apiPatch(`/v1/task/${id}/manual`)
      .then(() => {
        setApiState({status: 'success', error: null});
        queryClient.invalidateQueries({
          queryKey: [myTaskList('')],
          refetchType: 'active',
        });
        queryClient.invalidateQueries({
          queryKey: [getTaskCountApiPath()],
          refetchType: 'active',
        });
      })
      .catch(error => {
        setApiState({status: 'error', error});
      });
  };
  const handleCancel = () => {
    apiPost(`/v1/task/${id}/manual/cancel`)
      .then(() => {
        queryClient.invalidateQueries({
          queryKey: [myTaskList('')],
          refetchType: 'active',
        });
        queryClient.invalidateQueries({
          queryKey: [getTaskCountApiPath()],
          refetchType: 'active',
        });
        setOpen(false);
      })
      .catch(error => {
        setApiStateCancel({status: 'error', error});
      });
  };
  const handleComplete = () => {
    setIsClose(true);
  };
  const handleAbort = () => {
    setIsClose(false);
  };
  const handleEditCancel = () => {
    setOpen(false);
    setApiStateCancel({status: '', error: null});
  };
  const handleEditClose = () => {
    setOpen(false);
  };

  return (
    <Card className='m-auto p-0'>
      <Box className='flex justify-between px-8 py-4'>
        <Typography
          component='h4'
          id='select-option-title'
          className='text-white font-bold max-w-246'>
          Manual Task
        </Typography>
        <IconButton className='p-0'>
          <img
            src={IconClose}
            alt='close'
            onClick={handleCancel}
            className='w-34'
          />
        </IconButton>
      </Box>
      <Divider />
      <CardContent className='p-8'>
        <Stack
          direction='row'
          justifyContent='space-between'
          alignItems='center'>
          <Typography variant='h6'>
            {userId !== data?.created_by.id ? 'Requestor' : 'Assignee'}
          </Typography>
          <Typography variant='body1'>
            {userId !== data?.created_by.id
              ? data?.created_by.name
              : data.assigned_by?.name}
          </Typography>
        </Stack>
        <List
          sx={{background: `var(--blue_variant32)`}}
          className='my-3 radius-10 p-3'>
          {data &&
            data.comments?.map(value => (
              <ListItem key={value.id}>
                <ListItemAvatar>
                  <Avatar
                    alt={value.commented_by.name || 'Default Alt Text'}
                    src={
                      value.commented_by.profile
                        ? value.commented_by.profile
                        : ''
                    }
                  />
                </ListItemAvatar>
                <ListItemText
                  primary={value.comments}
                  secondary={
                    <React.Fragment>
                      <Typography
                        sx={{display: 'inline'}}
                        component='span'
                        variant='body2'
                        color='text.primary'>
                        {value.commented_by.name}
                      </Typography>
                      {` — ${dayjs(value.created_at).format(
                        'MM/DD/YYYY hh:mm:ss A',
                      )}`}
                    </React.Fragment>
                  }
                />
                {value.files.map(values => {
                  const fileExtension =
                    values.file_path?.split('.').pop()?.toLowerCase() || '';
                  const isAudioFile = ['mp3', 'wav', 'ogg'].includes(
                    fileExtension,
                  );
                  return (
                    <>
                      <Tooltip key={values.id} title={values.file_name}>
                        <Button
                          component='a'
                          href={values.file_path}
                          download={values.file_name}
                          target='_blank'
                          style={{textDecoration: 'none', color: 'inherit'}}
                          rel='noreferrer'
                          variant='contained'
                          className='blue-variant4  p-0 h-30 min-w-30 mr-2'
                          sx={{
                            border: `1px solid var(--gray-variant4)`,
                          }}>
                          {isAudioFile ? (
                            <AudioFileIcon />
                          ) : (
                            <DescriptionIcon />
                          )}
                        </Button>
                      </Tooltip>
                    </>
                  );
                })}
              </ListItem>
            ))}
        </List>
        {data?.status === 'completed' ? (
          <ArchiveManualTaskButton taskId={id} setOpen={setOpen} />
        ) : null}
        {data?.status !== 'completed' && (
          <>
            <DescriptionBox setTaskDescription={setTaskDescription} />
            <FileUploads
              selectedFiles={selectedFiles}
              setSelectedFiles={setSelectedFiles}
            />
            <Stack
              direction='row'
              spacing={2}
              className='mt-2 justify-center px-10'>
              <Button
                endIcon={<SendIcon />}
                disabled={!taskDescription}
                variant='contained'
                onClick={handleSubmit}>
                {userId !== data?.created_by.id ? 'Submit' : 'Send to Assignee'}
              </Button>
              {userId === data?.created_by.id && (
                <Button
                  variant='contained'
                  onClick={handleComplete}
                  className='min-w-150'>
                  Close
                </Button>
              )}
              <Button variant='outlined' onClick={handleCancel} color='info'>
                Cancel
              </Button>
            </Stack>
          </>
        )}
      </CardContent>
      {isClose && (
        <DialogConfirmSave
          name='Are you sure to Close this task ?'
          onSave={handleClose}
          onCancel={handleAbort}></DialogConfirmSave>
      )}
      {apiState.status !== '' && (
        <DialogApiRequestStatus
          apiState={apiState}
          onRetry={handleSubmit}
          onEdit={handleEdit}
          onClose={handleEditClose}
        />
      )}
      {apiStateCancel.status !== '' && (
        <DialogApiRequestStatus
          apiState={apiStateCancel}
          onRetry={handleCancel}
          onEdit={handleEditCancel}
          onClose={handleEditClose}
        />
      )}
    </Card>
  );
};
export default ManualTask;

export const ArchiveManualTaskButton = ({
  taskId,
  setOpen,
}: {
  taskId: number;
  setOpen: (val: boolean) => void;
}) => {
  const queryClient = useQueryClient();
  const [apiStateCancel, setApiStateCancel] = useState({
    status: '',
    error: null,
  });
  const handleEditCancel = () => {
    setApiStateCancel({status: '', error: null});
  };
  const handleEditClose = () => {
    setOpen(false);
  };
  const handleSubmit = () => {
    apiPost(`/v1/task/${taskId}/manual/archive`)
      .then(() => {
        queryClient.invalidateQueries({
          queryKey: [myTaskList('')],
          refetchType: 'active',
        });
        queryClient.invalidateQueries({
          queryKey: [getTaskCountApiPath()],
          refetchType: 'active',
        });
        setOpen(false);
      })
      .catch(error => {
        setApiStateCancel({status: 'error', error});
      });
  };
  return (
    <>
      <Button
        variant='contained'
        color='info'
        onClick={handleSubmit}
        sx={{mx: 'auto', display: 'block'}}>
        Archive
      </Button>
      {apiStateCancel.status !== '' && (
        <DialogApiRequestStatus
          apiState={apiStateCancel}
          onRetry={handleSubmit}
          onEdit={handleEditCancel}
          onClose={handleEditClose}
        />
      )}
    </>
  );
};

export const CompletionTimeInput = ({
  time,
  setTime,
  unit,
  setUnit,
}: {
  time: number | '';
  setTime: (val: number | '') => void;
  unit: string;
  setUnit: (val: string) => void;
}) => {
  const handleTimeChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;
    if (
      (value === '' ||
        (value.length <= 3 && unit === 'hours' && parseInt(value) <= 720) ||
        (value.length <= 2 && unit === 'days' && parseInt(value) <= 30)) &&
      value.indexOf('0') !== 0
    ) {
      setTime(value === '' ? '' : parseInt(value));
    }
  };

  const handleUnitChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setUnit(event.target.value);
    setTime('');
  };

  return (
    <Grid container>
      <Grid item xs={8}>
        <TextField
          fullWidth
          type='number'
          label='Completion Time'
          value={time}
          onChange={handleTimeChange}
          inputProps={{
            min: 1,
            max: unit === 'hours' ? 720 : 30, // 30 days equivalent to 720 hours
          }}
        />
      </Grid>
      <Grid item xs={4}>
        <TextField
          fullWidth
          select
          label='Unit'
          value={unit}
          onChange={handleUnitChange}
          style={{marginLeft: 10}}>
          <MenuItem value='hours'>Hours</MenuItem>
          <MenuItem value='days'>Days</MenuItem>
        </TextField>
      </Grid>
    </Grid>
  );
};
